import { SerenityConfigSummary } from "../generated/types";
import { fetchWithToken } from "../utils/fetchWithToken";

/**
 * Sauvegardes de config d'une Serenity : la box envoie son parameters.json chaque nuit, DATA
 * garde les 3 dernières. Le fichier téléchargé est celui de l'ancien système
 * (AAAA-MM-JJ-<bâtiment>.<version>.config.serenity, un tar.gz que la box réimporte par clé USB).
 */

const configsUrl = (uuid: string) =>
  `${process.env.REACT_APP_URL_SERVER_DATA}/devices/${encodeURIComponent(uuid)}/configs`;

/** La plus récente d'abord. Toute réponse autre que 200 est une erreur (jamais « aucune »). */
export const fetchSerenityConfigs = async (
  uuid: string
): Promise<SerenityConfigSummary[]> => {
  const response = await fetchWithToken(configsUrl(uuid), { method: "GET" });
  if (response.status !== 200) throw new Error(`HTTP ${response.status}`);
  const data: SerenityConfigSummary[] | null = await response.json();
  return data ?? [];
};

/**
 * Nom du fichier dans `Content-Disposition` : DATA écrit `filename="…"`, ou `filename*=utf-8''…`
 * quand le nom du bâtiment a des accents. Chaîne vide si l'en-tête est absent ou illisible.
 */
export const filenameFromContentDisposition = (header: string): string => {
  const encoded = /filename\*=utf-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return "";
    }
  }
  const quoted = /filename="((?:[^"\\]|\\.)*)"/i.exec(header);
  if (quoted) return quoted[1].replace(/\\(.)/g, "$1");
  const bare = /filename=([^;]+)/i.exec(header);
  return bare ? bare[1].trim() : "";
};

/** Télécharge une sauvegarde sous le nom choisi par DATA. */
export const downloadSerenityConfig = async (
  uuid: string,
  id: number
): Promise<void> => {
  const response = await fetchWithToken(`${configsUrl(uuid)}/${id}`, {
    method: "GET",
  });
  if (response.status !== 200) throw new Error(`HTTP ${response.status}`);
  const filename =
    filenameFromContentDisposition(
      response.headers.get("Content-Disposition") ?? ""
    ) || `${uuid}.config.serenity`;

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export type ConfigChange = {
  module: string;
  /** Depuis le module, sans le niveau `config` (ex. ["Ch1", "hysteresis"]). */
  path: string[];
  /** `renamed` : `path` mène à la liste, `before`/`after` sont l'ancien et le nouveau nom. */
  kind: "changed" | "added" | "removed" | "renamed";
  before: unknown;
  after: unknown;
};

export type ConfigComparison = {
  from: SerenityConfigSummary;
  to: SerenityConfigSummary;
  changes: ConfigChange[];
};

/** Ce qui change de `from` à `to`, calculé par DATA (réponse gardée en cache par le navigateur). */
export const compareSerenityConfigs = async (
  uuid: string,
  from: number,
  to: number
): Promise<ConfigComparison> => {
  const response = await fetchWithToken(
    `${configsUrl(uuid)}/compare?from=${from}&to=${to}`,
    { method: "GET" }
  );
  if (response.status !== 200) throw new Error(`HTTP ${response.status}`);
  const data: ConfigComparison = await response.json();
  return { ...data, changes: data.changes ?? [] };
};

/** Une valeur de réglage en une ligne : les objets et listes sont abrégés. */
export const formatConfigValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "object") return String(value);
  const json = JSON.stringify(value);
  return json.length > 60 ? `${json.slice(0, 57)}…` : json;
};

/** Changements regroupés par module, dans l'ordre reçu. */
export const groupChangesByModule = (
  changes: ConfigChange[]
): { module: string; changes: ConfigChange[] }[] =>
  changes.reduce<{ module: string; changes: ConfigChange[] }[]>(
    (groups, change) => {
      const last = groups[groups.length - 1];
      return last && last.module === change.module
        ? [
            ...groups.slice(0, -1),
            { ...last, changes: [...last.changes, change] },
          ]
        : [...groups, { module: change.module, changes: [change] }];
    },
    []
  );
